import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';

import { createFinancialMonth, getFinancialMonth } from '../../../apis/queries';

interface FinancialMonthFormProps {
  onShowToast: (message: string, variant: 'success' | 'danger') => void;
}

const FinancialMonth = ({ onShowToast }: FinancialMonthFormProps) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const mutation = useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      createFinancialMonth(startDate, endDate),
    onSuccess: () => {
      onShowToast('Financial month created successfully!', 'success');
    },
    onError: () => {
      onShowToast('Failed to create financial month. Please try again.', 'danger');
    }
  });

  const { data: financialMonthData } = useQuery({
    queryKey: ['lastFinancialMonthData'],
    queryFn: () => getFinancialMonth(),
    refetchOnWindowFocus: false
  });

  const onSaveFinancialMonth = (e: React.FormEvent) => {
    e.preventDefault();

    if (!endDate || new Date(endDate) <= new Date(startDate)) {
      onShowToast('End date must be after the start date.', 'danger');
      return;
    }

    mutation.mutate({ startDate, endDate });
  };

  return (
    <Card className="py-4 px-4" style={{ boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }}>
      <Row>
        <Form.Label className="mb-1">Financial month</Form.Label>
        <Form.Text>Set Financial month range</Form.Text>
      </Row>
      <Row className="mt-2">
        <Col xs={12} md={6}>
          <div className="form-group">
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="form-group">
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col>
          <Button size="sm" onClick={onSaveFinancialMonth} disabled={mutation.isPending} style={{ minWidth: '80px' }}>
            {mutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Saving
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Col>
      </Row>

      {/* Table to display financial month data */}
      <Row className="mt-4">
        <Col>
          <Form.Label>Financial Month Records</Form.Label>
          <Card style={{ boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Table size="sm" className="card-table table-nowrap" hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {financialMonthData?.results?.map((record, index: number) => (
                  <tr key={record._id.toString()}>
                    <td>{index + 1}</td>
                    <td>{new Date(record.startDate).toLocaleDateString()}</td>
                    <td>{new Date(record.endDate).toLocaleDateString()}</td>
                    <td>{record.isActive ? <Badge text="bg-primary-subtle">Current</Badge> : <></>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default FinancialMonth;
